import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, Globe, Users } from "lucide-react";
import { api } from "@/lib/api";
import { ResellerCard } from "@/components/ResellerCard";

export default function Resellers() {
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("all");

  const { data: resellers, isLoading } = useQuery({
    queryKey: ["resellers"],
    queryFn: async () => (await api.get("/resellers")).data,
  });
  const { data: countries } = useQuery({
    queryKey: ["reseller-countries"],
    queryFn: async () => (await api.get("/resellers/countries")).data,
  });

  const filtered = (resellers || []).filter((r) => {
    const matchCountry = country === "all" || r.country === country;
    const s = q.toLowerCase();
    const matchQ = !s || r.name.toLowerCase().includes(s) || (r.reseller_id || "").toLowerCase().includes(s) || (r.country || "").toLowerCase().includes(s);
    return matchCountry && matchQ;
  });

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-12">
      <div className="text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">Official & Verified</p>
        <h1 className="mt-2 font-display text-4xl font-extrabold text-white sm:text-5xl">Our Resellers</h1>
        <p className="mt-3 text-slate-400">Pick a trusted reseller and recharge instantly via WhatsApp.</p>
      </div>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-center">
        <div className="relative flex-1 sm:max-w-md">
          <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, ID or country..."
            data-testid="reseller-search-input"
            className="h-12 w-full rounded-xl border border-gold/20 bg-obsidian-card pl-12 pr-4 text-slate-100 outline-none focus:border-gold/60"
          />
        </div>
        <div className="relative sm:w-56">
          <Globe className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" />
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            data-testid="reseller-country-filter"
            className="h-12 w-full appearance-none rounded-xl border border-gold/20 bg-obsidian-card pl-12 pr-4 text-slate-100 outline-none focus:border-gold/60"
          >
            <option value="all">All Countries</option>
            {(countries || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {isLoading && <p className="col-span-full text-center text-slate-500">Loading...</p>}
        {!isLoading && filtered.length === 0 && (
          <div className="col-span-full flex flex-col items-center gap-3 py-16 text-center" data-testid="reseller-empty">
            <Users className="h-12 w-12 text-gold/40" />
            <p className="text-slate-500">No resellers match your search.</p>
          </div>
        )}
        {filtered.map((r) => <ResellerCard key={r.id} reseller={r} />)}
      </div>
    </div>
  );
}
